import { Injectable, inject } from '@angular/core';
import * as XLSX from 'xlsx';
import { firstValueFrom } from 'rxjs';
import { ReviewerService } from './reviewer.service';
import {
  REVIEW_TYPE_OPTIONS,
  REVIEW_TYPE_ID_MAP,
  BulkUploadIssueRating,
  BULK_UPLOAD_ISSUE_RATING_OPTIONS,
  BULK_UPLOAD_REPORT_STATUS_OPTIONS,
  BULK_UPLOAD_ACTION_PLAN_STATUS_OPTIONS,
  BULK_UPLOAD_ACTION_REQUIRED_BY_OPTIONS,
  BULK_UPLOAD_REP_LETTER_CONFLICT_OPTIONS,

  BULK_UPLOAD_ISSUE_FROM_GO_LIVE_OPTIONS,
} from '../../review-process/issue-management/issue-management.constants';

export interface BulkUploadValidationError {
  row?: number;
  sheet?: string;
  field?: string;
  error: string;
}

export interface BulkUploadValidationResult {
  validation: 'success' | 'failed';
  errors?: BulkUploadValidationError[];
}

// ── Payload interfaces ────────────────────────────────────────────────────────

export interface BulkUploadContext {
  reviewId?: number | null;
  userId?: string | null;
  reviewTypeId?: number | null;
}

export interface PayloadField {
  FieldName: string;
  FieldValue: string | number;
  DataType: 'INT' | 'NVARCHAR' | 'DATE' | 'BIT';
}

export interface PayloadRow {
  RowNumber: number;
  SubSheetName: string;
  fields: PayloadField[];
}

export interface IssueBulkUploadPayload {
  category: 'IssueBulkUpload';
  ReviewID: number;
  UserID: string;
  ReviewTypeId: number;
  IssueFieldsJSON: PayloadRow[];
  ActionFieldsJSON: PayloadRow[];
  MappingFieldsJSON: PayloadRow[][];
}

// ── Parsed sheet row type ─────────────────────────────────────────────────────

interface SheetRow {
  /** 1-based Excel row number for error reporting */
  rowNum: number;
  data: Record<string, any>;
}

interface ParsedSheets {
  review: SheetRow[];
  issues: SheetRow[];
  actionPlans: SheetRow[];
  mappings: SheetRow[];
}

const MAX_FILE_SIZE_MB = 20;
const MAX_RECORDS = 100;
const MAX_IDENTIFICATION_AGE_DAYS = 180;
const VALID_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const REQUIRED_SHEETS = ['1.REVIEW', '2.ISSUES', '3.ACTION_PLANS', '4.MAPPING'] as const;
const ALLOWED_VISIBLE_SUPPORT_SHEETS = new Set(['Instructions']);
const ISSUE_COUNT_SHEET = '2.ISSUES';

/** Row index (0-based) where actual column headers appear in each sheet. */
const HEADER_ROW_INDEX: Readonly<Record<string, number>> = {
  '1.REVIEW':       3,
  '2.ISSUES':       1,
  '3.ACTION_PLANS': 1,
  '4.MAPPING':      2,
};

/**
 * Expected column names per sheet, normalised (asterisks + line-breaks stripped).
 * These match the real template headers after normalisation.
 */
const REQUIRED_COLUMNS: Readonly<Record<string, string[]>> = {
  '1.REVIEW': ['Field', 'Your Input ▼', 'Accepted Values', 'Rule'],
  '2.ISSUES': [
    'Auto_Issue_ID', 'Issue Title', 'Issue Description', 'Identification Date',
    'R&C Comments', 'Action Required By', 'Issue Rating', 'Review Objective',
    'Recommendation', 'Broad Issue Category (L1)', 'Detailed Issue Category (L2)',
    'Risk Statement (L3)', 'RepLetter Conflict', 'RepLetter Questions with Conflict',
    'Financial Year', 'Quarter', 'Accenture Operation Service Line', 'Report Status',
    'Is this issue from Go-Live?', 'Legacy Issue Number (AuditBoard ID)',
  ],
  '3.ACTION_PLANS': [
    'Auto_AP_ID', 'Action Plan Status', 'Action Plan Description',
    'Closed date for the action plan', 'R&C Comments', 'Internal Dependency',
    'Target date for action plan / remediation action',
    'Client Dependency',
    'Legacy AP Number (AuditBoard ID)',
  ],
  '4.MAPPING': ['Mapping_Number', 'Auto_Issue_ID', 'Auto_AP_ID', 'Issue Rating'],
};

const REQUIRED_HEADER_ASTERISK_COUNTS: Readonly<Record<string, Readonly<Record<string, number>>>> = {
  '1.REVIEW': {},
  '2.ISSUES': {
    'Issue Title': 1,
    'Issue Description': 1,
    'Identification Date': 1,
    'R&C Comments': 1,
    'Action Required By': 1,
    'Issue Rating': 1,
    'Review Objective': 1,
    'Recommendation': 1,
    'Broad Issue Category (L1)': 1,
    'Detailed Issue Category (L2)': 1,
    'Risk Statement (L3)': 1,
    'RepLetter Conflict': 1,
    'Accenture Operation Service Line': 1,
    'Report Status': 1,
  },
  '3.ACTION_PLANS': {
    'Action Plan Status': 1,
    'Action Plan Description': 1,
    'Closed date for the action plan': 1,
    'R&C Comments': 1,
    'Target date for action plan / remediation action': 1,
  },
  '4.MAPPING': {
    'Auto_Issue_ID': 1,
    'Auto_AP_ID': 1,
  },
};

const VALID_REVIEW_TYPES = new Set<string>(REVIEW_TYPE_OPTIONS);
const VALID_ISSUE_RATINGS = new Set<string>(BULK_UPLOAD_ISSUE_RATING_OPTIONS);
const VALID_REPORT_STATUSES = new Set<string>(BULK_UPLOAD_REPORT_STATUS_OPTIONS);
const VALID_AP_STATUSES = new Set<string>(BULK_UPLOAD_ACTION_PLAN_STATUS_OPTIONS);
const VALID_ACTION_REQUIRED_BY = new Set<string>(BULK_UPLOAD_ACTION_REQUIRED_BY_OPTIONS);
const VALID_REP_LETTER_CONFLICT = new Set<string>(BULK_UPLOAD_REP_LETTER_CONFLICT_OPTIONS);
const VALID_QUARTERS = new Set<string>(['Q1', 'Q2', 'Q3', 'Q4']);

const VALID_GO_LIVE_OPTIONS = new Set<string>(BULK_UPLOAD_ISSUE_FROM_GO_LIVE_OPTIONS);
const FINANCIAL_YEAR_PATTERN = /^FY\d{2}$/i;
const STRICT_DATE_DD_MM_YYYY_PATTERN = /^(\d{2})\/(\d{2})\/(\d{4})$/;

const ISSUE_MANDATORY_FIELDS: readonly string[] = [
  'Issue Title', 'Issue Description', 'Identification Date', 'R&C Comments',
  'Action Required By', 'Issue Rating', 'Review Objective', 'Recommendation',
  'Broad Issue Category (L1)', 'Detailed Issue Category (L2)', 'Risk Statement (L3)',
  'RepLetter Conflict', 'Accenture Operation Service Line', 'Report Status',
];

const AP_MANDATORY_FIELDS: readonly string[] = [
  'Action Plan Status', 'Action Plan Description', 'R&C Comments',
  'Closed date for the action plan',
  'Target date for action plan / remediation action',
];

const MAPPING_MANDATORY_FIELDS: readonly string[] = [
  'Auto_Issue_ID',
  'Auto_AP_ID',
];

@Injectable({ providedIn: 'root' })
export class BulkUploadValidationService {

  private readonly reviewerService = inject(ReviewerService);
  private cachedFinancialYears: Set<string> | null = null;

  payload: IssueBulkUploadPayload | null = null;

  constructor() {
    this.loadFinancialYears();
  }

  private async loadFinancialYears(): Promise<boolean> {
    if (this.cachedFinancialYears) return true;
    try {
      const years = await firstValueFrom(this.reviewerService.getPurposedYear()) as Array<{ DisplayName?: string; ProposedYear?: number; Name?: string }>;
      this.cachedFinancialYears = new Set(
        years
          .map((year) => String(year?.DisplayName ?? year?.Name ?? year?.ProposedYear ?? '').trim())
          .filter((value) => value !== ''),
      );
      return true;
    } catch (error) {
      console.error('Failed to load financial year options for bulk upload validation:', error);
      return false;
    }
  }

  normalizeBulkUploadApiErrors(response: any): BulkUploadValidationError[] {
    const candidates = Array.isArray(response)
      ? response
      : Array.isArray(response?.errors)
        ? response.errors
        : response && typeof response === 'object'
          ? [response]
          : [];

    return candidates.flatMap((item: any) => {
      if (!item || typeof item !== 'object') {
        return [];
      }

      const rowValue = item.RowNumber ?? item.row ?? item.Row ?? undefined;
      const sheetValue = item.SubSheetName ?? item.sheet ?? item.SubSheet ?? undefined;
      const fieldValue = item.FieldName ?? item.field ?? item.Field ?? undefined;
      const errorValue = item.Message ?? item.message ?? item.error ?? item.Error ?? undefined;

      const row = rowValue === undefined || rowValue === null || rowValue === '' ? undefined : Number(rowValue);
      const sheet = sheetValue === undefined || sheetValue === null || sheetValue === '' ? undefined : String(sheetValue);
      const field = fieldValue === undefined || fieldValue === null || fieldValue === '' ? undefined : String(fieldValue);
      const error = errorValue === undefined || errorValue === null || errorValue === '' ? 'Validation failed.' : String(errorValue);

      if (row === undefined && sheet === undefined && field === undefined && !error) {
        return [];
      }

      return [{
        row: Number.isFinite(row) ? row : undefined,
        sheet,
        field,
        error,
      }];
    });
  }

  async validateBulkUploadFile(file: File, context?: BulkUploadContext): Promise<BulkUploadValidationResult> {
    this.payload = null;

    // Phase 1a: file meta (extension, MIME, size)
    const fileError = this.validateFile(file);
    if (fileError) {
      return { validation: 'failed', errors: [{ error: fileError }] };
    }

    let workbook: XLSX.WorkBook;
    try {
      const buffer = await file.arrayBuffer();
      workbook = XLSX.read(buffer, { type: 'array' });
    } catch {
      return {
        validation: 'failed',
        errors: [{ error: 'Unable to read the file. Ensure it is a valid .xlsx workbook.' }],
      };
    }

    // Phase 1b: required sheets (tab availability / template version proxy)
    const sheetErrors = this.validateSheets(workbook);
    if (sheetErrors.length) {
      return { validation: 'failed', errors: sheetErrors };
    }

    // Phase 1c: column header naming conventions per sheet
    const headerErrors = this.validateColumnHeaders(workbook);
    if (headerErrors.length) {
      return { validation: 'failed', errors: headerErrors };
    }

    // Phase 1d: total issue records cap in 2.ISSUES
    const totalIssues = this.getNonEmptyDataRowCount(workbook, ISSUE_COUNT_SHEET);
    if (totalIssues === 0) {
      return {
        validation: 'failed',
        errors: [{
          sheet: ISSUE_COUNT_SHEET,
          error: 'The uploaded file has no issue records. Please add at least one issue and try again.',
        }],
      };
    }

    if (totalIssues > MAX_RECORDS) {
      return {
        validation: 'failed',
        errors: [
          {
            error: `Record limit exceeded. Maximum issues ${MAX_RECORDS} records allowed per upload. Please split and upload separately`,
          },
        ],
      };
    }

    const yearsLoaded = await this.loadFinancialYears();
    if (!yearsLoaded) {
      return {
        validation: 'failed',
        errors: [{ error: 'Failed to retrieve Financial Year options. Please refresh the page and try again.' }],
      };
    }

    // Phase 2: row-level data validations (Scenario 4)
    const sheets = this.parseAllSheets(workbook);
    const reviewTypeErrors: BulkUploadValidationError[] = [];
    this.validateReviewSheet(sheets.review, reviewTypeErrors);
    if (reviewTypeErrors.length) {
      return { validation: 'failed', errors: reviewTypeErrors };
    }

    const rowErrors = this.validateAllSheets(sheets);
    const reviewTypeMismatchError = this.getReviewTypeMismatchError(sheets, context);
    if (reviewTypeMismatchError) {
      return { validation: 'failed', errors: [reviewTypeMismatchError] };
    }

    if (sheets.actionPlans.length === 0) {
      const actionPlanErrors = ['Auto_AP_ID', ...AP_MANDATORY_FIELDS].map(field => ({
        row: HEADER_ROW_INDEX['3.ACTION_PLANS'] + 2,
        sheet: '3.ACTION_PLANS',
        field,
        error: `"${field}" is mandatory.`,
      }));
      const mappingErrorIndex = rowErrors.findIndex(error => error.sheet === '4.MAPPING');
      rowErrors.splice(mappingErrorIndex === -1 ? rowErrors.length : mappingErrorIndex, 0, ...actionPlanErrors);
    }

    this.sortValidationErrors(rowErrors);

    if (rowErrors.length) {
      return { validation: 'failed', errors: rowErrors };
    }

    this.payload = this.buildPayload(sheets, context);
    console.log('Bulk upload payload (object):', this.payload);
    // console.log('Bulk upload payload (JSON):', JSON.stringify(this.payload, null, 2));
    return { validation: 'success' };
  }

  // ── Phase 1 helpers ──────────────────────────────────────────────────────────

  private validateFile(file: File): string | null {
    const isXlsx = file.name.toLowerCase().endsWith('.xlsx');
    if (!isXlsx || (file.type && file.type !== VALID_MIME)) {
      return 'Invalid file format. Please upload a .xlsx file only.';
    }
    if (file.size === 0) {
      return 'The uploaded file is empty. Please upload a valid .xlsx file.';
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      return `File size exceeds ${MAX_FILE_SIZE_MB} MB limit. Please split the file and upload separately.`;
    }
    return null;
  }

  private validateSheets(workbook: XLSX.WorkBook): BulkUploadValidationError[] {
    const missing = REQUIRED_SHEETS.filter(s => !workbook.SheetNames.includes(s));
    const extraVisibleSheets = workbook.SheetNames.filter((sheetName, index) => {
      if (REQUIRED_SHEETS.includes(sheetName as typeof REQUIRED_SHEETS[number])) return false;
      if (ALLOWED_VISIBLE_SUPPORT_SHEETS.has(sheetName)) return false;
      return Number(workbook.Workbook?.Sheets?.[index]?.Hidden ?? 0) === 0;
    });

    if (!missing.length && !extraVisibleSheets.length) return [];
    return [{
      error: 'Outdated template version. Please download the latest template and try again.',
    }];
  }

  private validateColumnHeaders(workbook: XLSX.WorkBook): BulkUploadValidationError[] {
    let hasInvalidHeaders = false;
    for (const sheet of REQUIRED_SHEETS) {
      const rawRows: any[][] = XLSX.utils.sheet_to_json(workbook.Sheets[sheet], { header: 1, defval: '' });
      const rawHeaderRow = rawRows[HEADER_ROW_INDEX[sheet]] ?? [];
      const headerRow = rawHeaderRow.map((c: any) => this.normalizeHeader(c));
      const missing = REQUIRED_COLUMNS[sheet].filter(c => !headerRow.includes(c));
      const extra = headerRow.filter(header => header !== '' && !REQUIRED_COLUMNS[sheet].includes(header));
      const populatedHeaders = headerRow.filter(header => header !== '');
      const hasColumnOrderMismatch = populatedHeaders.length !== REQUIRED_COLUMNS[sheet].length
        || populatedHeaders.some((header, index) => header !== REQUIRED_COLUMNS[sheet][index]);
      const invalidAsteriskMarkers = rawHeaderRow.some((header: any) => {
        const normalizedHeader = this.normalizeHeader(header);
        if (!normalizedHeader || !REQUIRED_COLUMNS[sheet].includes(normalizedHeader)) return false;
        const expectedCount = REQUIRED_HEADER_ASTERISK_COUNTS[sheet][normalizedHeader] ?? 0;
        const actualCount = (String(header ?? '').match(/\*/g) ?? []).length;
        return actualCount !== expectedCount;
      });
      if (missing.length || extra.length || hasColumnOrderMismatch || invalidAsteriskMarkers) {
        hasInvalidHeaders = true;
      }
    }
    return hasInvalidHeaders
      ? [{ error: 'Outdated template version. Please download the latest template and try again.' }]
      : [];
  }

  private getNonEmptyDataRowCount(workbook: XLSX.WorkBook, sheet: string): number {
    const rawRows: any[][] = XLSX.utils.sheet_to_json(workbook.Sheets[sheet], { header: 1, defval: '' });
    const dataStartIndex = HEADER_ROW_INDEX[sheet] + 1;
    return rawRows.slice(dataStartIndex).filter(
      row => Array.isArray(row)
        && row.some(cell => String(cell ?? '').trim() !== '')
        && !(sheet === '2.ISSUES' && this.isTemplateInstructionRow(row)),
    ).length;
  }

  // ── Phase 2: parse ───────────────────────────────────────────────────────────

  private parseAllSheets(workbook: XLSX.WorkBook): ParsedSheets {
    return {
      review:      this.parseSheet(workbook, '1.REVIEW'),
      issues:      this.parseSheet(workbook, '2.ISSUES'),
      actionPlans: this.parseSheet(workbook, '3.ACTION_PLANS'),
      mappings:    this.parseSheet(workbook, '4.MAPPING'),
    };
  }

  private parseSheet(workbook: XLSX.WorkBook, sheetName: string): SheetRow[] {
    const rawRows: any[][] = XLSX.utils.sheet_to_json(
      workbook.Sheets[sheetName], { header: 1, defval: '' },
    );
    const headerRowIdx = HEADER_ROW_INDEX[sheetName];
    const headers: string[] = (rawRows[headerRowIdx] ?? []).map((c: any) => this.normalizeHeader(c));
    const dataRows = rawRows.slice(headerRowIdx + 1);

    return dataRows
      .map((cells, idx) => {
        const data: Record<string, any> = {};
        headers.forEach((h, col) => { data[h] = cells[col] ?? ''; });
        return { rowNum: headerRowIdx + idx + 2, data };
      })
      .filter(r => Object.values(r.data).some(v => String(v ?? '').trim() !== ''))
      .filter(r => !(['2.ISSUES', '3.ACTION_PLANS', '4.MAPPING'].includes(sheetName)
        && this.isTemplateInstructionRow(Object.values(r.data))));
  }

  private isTemplateInstructionRow(row: any[]): boolean {
    const firstCell = String(row[0] ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
    return firstCell === 'autogeneratedreadonly' || firstCell === 'autoreadonly';
  }

  // ── Phase 2: validate ────────────────────────────────────────────────────────

  private validateAllSheets(sheets: ParsedSheets): BulkUploadValidationError[] {
    const errors: BulkUploadValidationError[] = [];
    const reviewType = this.validateReviewSheet(sheets.review, errors);
    const ratingMap  = this.validateIssuesSheet(sheets.issues, reviewType, errors);
    const apStatusMap = this.validateActionPlansSheet(sheets.actionPlans, errors);
    this.validateMappingSheet(sheets.mappings, ratingMap, apStatusMap, errors);
    return errors;
  }

  private sortValidationErrors(errors: BulkUploadValidationError[]): void {
    const sheetOrder = new Map<string, number>(REQUIRED_SHEETS.map((sheet, index) => [sheet, index]));

    errors.sort((left, right) => {
      const sheetDifference = (sheetOrder.get(left.sheet ?? '') ?? Number.MAX_SAFE_INTEGER)
        - (sheetOrder.get(right.sheet ?? '') ?? Number.MAX_SAFE_INTEGER);
      if (sheetDifference !== 0) return sheetDifference;

      const rowDifference = (left.row ?? Number.MAX_SAFE_INTEGER) - (right.row ?? Number.MAX_SAFE_INTEGER);
      if (rowDifference !== 0) return rowDifference;

      const leftFieldOrder = left.sheet ? REQUIRED_COLUMNS[left.sheet]?.indexOf(left.field ?? '') ?? -1 : -1;
      const rightFieldOrder = right.sheet ? REQUIRED_COLUMNS[right.sheet]?.indexOf(right.field ?? '') ?? -1 : -1;
      return (leftFieldOrder < 0 ? Number.MAX_SAFE_INTEGER : leftFieldOrder)
        - (rightFieldOrder < 0 ? Number.MAX_SAFE_INTEGER : rightFieldOrder);
    });
  }

  private getReviewTypeMismatchError(
    sheets: ParsedSheets,
    context?: BulkUploadContext,
  ): BulkUploadValidationError | null {
    const currentReviewTypeId = Number(context?.reviewTypeId ?? NaN);
    const finalReviewTypeId = this.resolveReviewTypeIdFromSheet(sheets);
    if (!Number.isFinite(currentReviewTypeId) || currentReviewTypeId <= 0 || currentReviewTypeId === finalReviewTypeId) {
      return null;
    }

    const reviewTypeRow = sheets.review.find(
      r => String(r.data['Field'] ?? '').replace(/\*/g, '').trim() === 'Review Type',
    );
    const reviewTypeRowNum = reviewTypeRow?.rowNum ?? sheets.review[0]?.rowNum;
    const currentReviewType = Object.entries(REVIEW_TYPE_ID_MAP)
      .find(([, id]) => id === currentReviewTypeId)?.[0] ?? `ReviewTypeID ${currentReviewTypeId}`;
    const finalReviewType = Object.entries(REVIEW_TYPE_ID_MAP)
      .find(([, id]) => id === finalReviewTypeId)?.[0] ?? `ReviewTypeID ${finalReviewTypeId}`;

    return {
      row: reviewTypeRowNum,
      sheet: '1.REVIEW',
      field: 'Review Type',
      error: `Selected review type does not match the current review. Current review type: ${currentReviewType}. Uploaded file review type: ${finalReviewType}.`,
    };
  }

  private resolveReviewTypeIdFromSheet(sheets: ParsedSheets): number {
    const reviewTypeRow = sheets.review.find(
      r => String(r.data['Field'] ?? '').replace(/\*/g, '').trim() === 'Review Type',
    );
    const reviewType = String(reviewTypeRow?.data['Your Input ▼'] ?? '').trim();

    if (!reviewType) {
      return 1;
    }

    return REVIEW_TYPE_ID_MAP[reviewType] ?? 1;
  }

  private validateReviewSheet(rows: SheetRow[], errors: BulkUploadValidationError[]): string {
    // Field cell values may carry a trailing asterisk (e.g. "Review Type *") — strip it for comparison.
    const reviewTypeRow = rows.find(r => String(r.data['Field'] ?? '').replace(/\*/g, '').trim() === 'Review Type');
    const reviewType = String(reviewTypeRow?.data['Your Input ▼'] ?? '').trim();
    const rowNumber = reviewTypeRow?.rowNum ?? rows[0]?.rowNum;
    if (!reviewType) {
      errors.push({
        row: rowNumber,
        sheet: '1.REVIEW',
        field: 'Review Type',
        error: '"Review Type" is mandatory.',
      });
    } else if (!VALID_REVIEW_TYPES.has(reviewType)) {
      errors.push({
        row: rowNumber,
        sheet: '1.REVIEW',
        field: 'Review Type',
        error: `"Review Type" is not a valid value. Accepted values: ${[...VALID_REVIEW_TYPES].join(' / ')}.`,
      });
    }
    return reviewType;
  }

  private validateIssuesSheet(
    rows: SheetRow[],
    reviewType: string,
    errors: BulkUploadValidationError[],
  ): Map<string, string> {
    const ratingMap = new Map<string, string>();
    const isGoLive = /go.?live/i.test(reviewType);

    for (const { rowNum, data } of rows) {
      const e = (field: string, msg: string) =>
        errors.push({ row: rowNum, sheet: '2.ISSUES', field, error: msg });

      // Mandatory fields
      for (const field of ISSUE_MANDATORY_FIELDS) {
        if (!String(data[field] ?? '').trim()) {
          e(field, `"${field}" is mandatory.`);
        }
      }

      const issueId    = String(data['Auto_Issue_ID'] ?? '').trim();
      const issueRating = String(data['Issue Rating'] ?? '').trim();

      if (!issueId) {
        e('Auto_Issue_ID', '"Auto_Issue_ID" is mandatory.');
      } else {
        ratingMap.set(issueId, issueRating);
      }

      // Identification Date — valid date and ≤ 180 days old
      const idDateVal = data['Identification Date'];
      if (String(idDateVal ?? '').trim()) {
        const parsed = this.parseExcelDate(idDateVal);
        if (!parsed) {
          e('Identification Date', '"Identification Date" must be a valid date.');
        } else if (this.daysAgo(parsed) > MAX_IDENTIFICATION_AGE_DAYS) {
          e('Identification Date', `"Identification Date" cannot be older than ${MAX_IDENTIFICATION_AGE_DAYS} days.`);
        }
      }

      // Action Required By valid values
      const actionReqBy = String(data['Action Required By'] ?? '').trim();
      if (actionReqBy && !VALID_ACTION_REQUIRED_BY.has(actionReqBy)) {
        e('Action Required By', `"Action Required By" is not a valid value. Accepted values: ${[...VALID_ACTION_REQUIRED_BY].join(' / ')}.`);
      }

      // Issue Rating valid values
      if (issueRating && !VALID_ISSUE_RATINGS.has(issueRating)) {
        e('Issue Rating', `"Issue Rating" is not a valid value. Accepted values: ${[...VALID_ISSUE_RATINGS].join(' / ')}.`);
      }

      // RepLetter Conflict valid values
      const repConflict = String(data['RepLetter Conflict'] ?? '').trim();
      if (repConflict && !VALID_REP_LETTER_CONFLICT.has(repConflict)) {
        e('RepLetter Conflict', `"RepLetter Conflict" is not a valid value. Accepted values: ${[...VALID_REP_LETTER_CONFLICT].join(' / ')}.`);
      }

      // Report Status valid values
      const reportStatus = String(data['Report Status'] ?? '').trim();
      if (reportStatus && !VALID_REPORT_STATUSES.has(reportStatus)) {
        e('Report Status', `"Report Status" is not a valid value. Accepted values: ${[...VALID_REPORT_STATUSES].join(' / ')}.`);
      }

      // RepLetter Conflict = Yes → conditional mandatory fields
      if (String(data['RepLetter Conflict'] ?? '').trim() === 'Yes') {
        for (const field of ['RepLetter Questions with Conflict', 'Financial Year', 'Quarter']) {
          if (!String(data[field] ?? '').trim()) {
            e(field, `"${field}" is mandatory when RepLetter Conflict = Yes.`);
          }
        }

        const financialYear = String(data['Financial Year'] ?? '').trim();
        if (financialYear) {
          const normalizedYear = financialYear.toUpperCase();
          if (!FINANCIAL_YEAR_PATTERN.test(normalizedYear)) {
            e('Financial Year', '"Financial Year" must be in FY00 format (e.g. FY25).');
          } else if (this.cachedFinancialYears && !this.cachedFinancialYears.has(normalizedYear)) {
            e('Financial Year', '"Financial Year" is not available in the system. Please select a valid fiscal year.');
          }
        }

        const quarter = String(data['Quarter'] ?? '').trim().toUpperCase();
        if (quarter && !VALID_QUARTERS.has(quarter)) {
          e('Quarter', '"Quarter" is not a valid value. Accepted values: Q1 / Q2 / Q3 / Q4.');
        }
      }

      // Go-Live review type → conditional mandatory field
      if (isGoLive && !String(data['Is this issue from Go-Live?'] ?? '').trim()) {
        e('Is this issue from Go-Live?', '"Is this issue from Go-Live?" is mandatory for Go-Live review type.');
      }

      // Go-Live option valid values
      const goLiveVal = String(data['Is this issue from Go-Live?'] ?? '').trim();
      if (goLiveVal && !VALID_GO_LIVE_OPTIONS.has(goLiveVal)) {
        e('Is this issue from Go-Live?', `"Is this issue from Go-Live?" is not a valid value. Accepted values: ${[...VALID_GO_LIVE_OPTIONS].join(' / ')}.`);
      }

      // Legacy Issue Number — numeric only if filled
      const legacy = String(data['Legacy Issue Number (AuditBoard ID)'] ?? '').trim();
      if (legacy && !/^\d+$/.test(legacy)) {
        e('Legacy Issue Number (AuditBoard ID)', '"Legacy Issue Number (AuditBoard ID)" must be numeric.');
      }
    }

    return ratingMap;
  }

  private validateActionPlansSheet(
    rows: SheetRow[],
    errors: BulkUploadValidationError[],
  ): Map<string, string> {
    const apStatusMap = new Map<string, string>();

    for (const { rowNum, data } of rows) {
      const e = (field: string, msg: string) =>
        errors.push({ row: rowNum, sheet: '3.ACTION_PLANS', field, error: msg });

      // Mandatory fields
      for (const field of AP_MANDATORY_FIELDS) {
        if (!String(data[field] ?? '').trim()) {
          e(field, `"${field}" is mandatory.`);
        }
      }

      const apId     = String(data['Auto_AP_ID'] ?? '').trim();
      const apStatus = String(data['Action Plan Status'] ?? '').trim();

      if (!apId) {
        e('Auto_AP_ID', '"Auto_AP_ID" is mandatory.');
      } else {
        apStatusMap.set(apId, apStatus);
      }

      // Action Plan Status valid values
      if (apStatus && !VALID_AP_STATUSES.has(apStatus)) {
        e('Action Plan Status', `"Action Plan Status" is not a valid value. Accepted values: ${[...VALID_AP_STATUSES].join(' / ')}.`);
      }

      // Closed date — if filled, must be a valid date and ≤ 180 days old
      const closedVal = data['Closed date for the action plan'];
      if (String(closedVal ?? '').trim()) {
        const parsed = this.parseExcelDate(closedVal);
        if (!parsed) {
          e('Closed date for the action plan', '"Closed date for the action plan" must be a valid date.');
        } else if (this.daysAgo(parsed) > MAX_IDENTIFICATION_AGE_DAYS) {
          e('Closed date for the action plan', `"Closed date for the action plan" cannot be older than ${MAX_IDENTIFICATION_AGE_DAYS} days.`);
        }
      }

      // Target date — same rule
      const targetVal = data['Target date for action plan / remediation action'];
      if (String(targetVal ?? '').trim()) {
        const parsed = this.parseExcelDate(targetVal);
        if (!parsed) {
          e('Target date for action plan / remediation action', '"Target date for action plan / remediation action" must be a valid date.');
        } else if (this.daysAgo(parsed) > MAX_IDENTIFICATION_AGE_DAYS) {
          e('Target date for action plan / remediation action', `"Target date for action plan / remediation action" cannot be older than ${MAX_IDENTIFICATION_AGE_DAYS} days.`);
        }
      }

      // Legacy AP Number — numeric only if filled
      const legacy = String(data['Legacy AP Number (AuditBoard ID)'] ?? '').trim();
      if (legacy && !/^\d+$/.test(legacy)) {
        e('Legacy AP Number (AuditBoard ID)', '"Legacy AP Number (AuditBoard ID)" must be numeric.');
      }
    }

    return apStatusMap;
  }

  private validateMappingSheet(
    rows: SheetRow[],
    ratingMap: Map<string, string>,
    apStatusMap: Map<string, string>,
    errors: BulkUploadValidationError[],
  ): void {
    if (rows.length === 0) {
      for (const field of MAPPING_MANDATORY_FIELDS) {
        errors.push({
          row: HEADER_ROW_INDEX['4.MAPPING'] + 4,
          sheet: '4.MAPPING',
          field,
          error: `"${field}" is mandatory.`,
        });
      }
      return;
    }

    const seen = new Map<string, number>();

    for (const { rowNum, data } of rows) {
      const issueId = String(data['Auto_Issue_ID'] ?? '').trim();
      const apId    = String(data['Auto_AP_ID'] ?? '').trim();

      const e = (field: string, msg: string) =>
        errors.push({ row: rowNum, sheet: '4.MAPPING', field, error: msg });

      for (const field of MAPPING_MANDATORY_FIELDS) {
        if (!String(data[field] ?? '').trim()) {
          e(field, `"${field}" is mandatory.`);
        }
      }

      // Normal mandatory validation flow, aligned with Issues and Action Plans tabs.
      // A blank Auto_Issue_ID or Auto_AP_ID fails validation; no custom partial-row rule is enforced here.
      if (!issueId || !apId) continue;

      // Skip template placeholder/instruction rows (IDs contain whitespace or are blank)
      if (/\s/.test(issueId) || /\s/.test(apId)) continue;

      // Cross-sheet existence checks
      if (issueId && !ratingMap.has(issueId)) {
        e('Auto_Issue_ID', `"${issueId}" does not exist in the 2.ISSUES sheet.`);
      }
      if (apId && !apStatusMap.has(apId)) {
        e('Auto_AP_ID', `"${apId}" does not exist in the 3.ACTION_PLANS sheet.`);
      }

      // Duplicate mapping check
      const comboKey = `${issueId}||${apId}`;
      const firstRow = seen.get(comboKey);
      if (firstRow !== undefined) {
        e('Auto_Issue_ID', `Duplicate mapping — ${issueId} and ${apId} already mapped in Row ${firstRow}.`);
      } else {
        seen.set(comboKey, rowNum);
      }

      // Blue constraint (both directions): Blue-rated issue may only map to a Closed-Blue Not Tracked AP,
      // and a Closed-Blue Not Tracked AP may only be mapped to a Blue-rated issue.
      if (issueId && apId) {
        const rating   = ratingMap.get(issueId);
        const apStatus = apStatusMap.get(apId);
        const normalize = (s: string | undefined) => (s ?? '').toLowerCase().replace(/\s+/g, ' ').trim();
        const isBlueIssue = normalize(rating) === 'blue';
        const isBlueAp    = normalize(apStatus) === normalize('Closed-Blue Not Tracked');
        if (isBlueIssue && !isBlueAp) {
          e('Auto_AP_ID', `Blue rated Issue "${issueId}" can only be mapped to a "Closed-Blue Not Tracked" Action Plan.`);
        } else if (isBlueAp && rating && !isBlueIssue) {
          e('Auto_AP_ID', `"Closed-Blue Not Tracked" Action Plan "${apId}" can only be mapped to a Blue rated Issue. Issue "${issueId}" is rated "${rating}".`);
        }
      }
    }
  }

  // ── Phase 3: build payload ───────────────────────────────────────────────────

  private buildPayload(sheets: ParsedSheets, context?: BulkUploadContext): IssueBulkUploadPayload {
    const reviewId = Number(context?.reviewId ?? 0);
    const userId = String(context?.userId ?? '').trim();
    const reviewTypeId = this.resolveReviewTypeIdFromSheet(sheets);

    const IssueFieldsJSON: PayloadRow[] = sheets.issues.map(({ rowNum, data }, index) => {
      const repConflictRaw = String(data['RepLetter Conflict'] ?? '').trim();
      const repConflict = repConflictRaw.toLowerCase() === 'yes' ? 'Yes' : repConflictRaw.toLowerCase() === 'no' ? 'No' : 'Not Applicable';
      const repQuestions = String(data['RepLetter Questions with Conflict'] ?? '').trim();

      return {
        RowNumber: rowNum,
        SubSheetName: 'Issues',
        fields: [
          // Generated by GitHub Copilot - Aug-19-2026
          this.field('IssueID', this.toInt(data['Auto_Issue_ID']), 'INT'),
          this.field('IssueName', '', 'NVARCHAR'),
          this.field('IssueTitle', data['Issue Title'], 'NVARCHAR'),
          this.field('IssueDescription', data['Issue Description'], 'NVARCHAR'),
          this.field('ReviewObjective', data['Review Objective'], 'NVARCHAR'),
          this.field('Recommendation', data['Recommendation'], 'NVARCHAR'),
          this.field('ActionRequiredBy', data['Action Required By'], 'NVARCHAR'),
          this.field('BroadIssueCategoryL1', data['Broad Issue Category (L1)'], 'NVARCHAR'),
          this.field('DetailedIssueCategoryL2', data['Detailed Issue Category (L2)'], 'NVARCHAR'),
          this.field('RiskStatementL3', data['Risk Statement (L3)'], 'NVARCHAR'),
          this.field('IdentificationDate', this.formatDateCellIssue(data['Identification Date']), 'DATE'),
          this.field('IssueCloseDueDate', '', 'DATE'),
          this.field('IssueRating', data['Issue Rating'], 'NVARCHAR'),
          this.field('ReportStatus', data['Report Status'], 'NVARCHAR'),
          this.field('RepLetterConflict', repConflict, 'NVARCHAR'),
          this.field('RepLetter Questions with Conflict', repQuestions, 'NVARCHAR'),
          this.field('Accenture Operation Service Line', data['Accenture Operation Service Line'], 'NVARCHAR'),
          this.field('RCReviewer', '', 'NVARCHAR'),
          this.field('RCSupervisor', '', 'NVARCHAR'),
          this.field('RCManager', '', 'NVARCHAR'),
          this.field('RCComments', data['R&C Comments'], 'NVARCHAR'),
          this.field('LocationReviewedImpacted', '', 'NVARCHAR'),
          this.field('LocationNames', '', 'NVARCHAR'),
          this.field('SubscriberNames', '', 'NVARCHAR'),
          this.field('LegacyIssueNumber', data['Legacy Issue Number (AuditBoard ID)'], 'NVARCHAR'),
          this.field('IssueFromGoLive', data['Is this issue from Go-Live?'], 'NVARCHAR'),
        ],
      };
    });

    const ActionFieldsJSON: PayloadRow[] = sheets.actionPlans.map(({ rowNum, data }, index) => {
      return {
        RowNumber: rowNum,
        SubSheetName: 'Action_Plans',
        fields: [
          // Generated by GitHub Copilot - Aug-19-2026
          this.field('ActionPlanID', this.toInt(data['Auto_AP_ID']), 'INT'),
          this.field('ActionPlanName', '', 'NVARCHAR'),
          this.field('ActionPlanStatus', '', 'NVARCHAR'),
          this.field('AP_Description', data['Action Plan Description'], 'NVARCHAR'),
          this.field('AP_OwnerEmail', '', 'NVARCHAR'),
          this.field('AP_TargetDate', this.formatDateCell(data['Target date for action plan / remediation action']), 'DATE'),
          this.field('AP_Status', data['Action Plan Status'], 'NVARCHAR'),
          this.field('AP_InternalDependency', data['Internal Dependency'], 'NVARCHAR'),
          this.field('AP_ClientDependency', data['Client Dependency'], 'NVARCHAR'),
          this.field('AP_ClosedDate', this.formatDateCell(data['Closed date for the action plan']), 'DATE'),
          this.field('AP_RevisedTargetDate', '', 'DATE'),
          this.field('LegacyAPNumber', data['Legacy AP Number (AuditBoard ID)'], 'NVARCHAR'),
          this.field('AP_RCComments', data['R&C Comments'], 'NVARCHAR'),
          this.field('TargetDateclientinfo', '', 'DATE'),
          this.field('DocumentsJSON', '[]', 'NVARCHAR'),
        ],
      };
    });

    const MappingFieldsJSON: PayloadRow[][] = sheets.mappings
      .filter(({ data }) => {
        const issueId = String(data['Auto_Issue_ID'] ?? '').trim();
        const apId = String(data['Auto_AP_ID'] ?? '').trim();
        return issueId && apId && !/\s/.test(issueId) && !/\s/.test(apId);
      })
      .map(({ rowNum, data }) => [{
        RowNumber: rowNum,
        SubSheetName: 'Mapping',
        fields: [
          this.field('IssueID', this.toInt(data['Auto_Issue_ID']), 'INT'),
          this.field('ActionPlanID', this.toInt(data['Auto_AP_ID']), 'INT'),
          this.field('IssueRating', data['Issue Rating'], 'NVARCHAR'),
        ],
      }]);

    return {
      category: 'IssueBulkUpload',
      ReviewID: isNaN(reviewId) ? 0 : reviewId,
      UserID: userId,
      ReviewTypeId: isNaN(reviewTypeId) ? 1 : reviewTypeId,
      IssueFieldsJSON,
      ActionFieldsJSON,
      MappingFieldsJSON,
    };
  }

  private field(name: string, value: any, type: PayloadField['DataType']): PayloadField {
    if (type === 'INT') {
      return {
        FieldName: name,
        FieldValue: this.toInt(value),
        DataType: type,
      };
    }

    if (type === 'BIT') {
      const normalized = String(value ?? '').trim().toLowerCase();
      const bitValue = normalized === 'true' || normalized === '1' || normalized === 'yes' ? 'true' : 'false';
      return {
        FieldName: name,
        FieldValue: bitValue,
        DataType: type,
      };
    }

    return {
      FieldName: name,
      FieldValue: String(value ?? '').trim(),
      DataType: type,
    };
  }

  private toInt(value: any): number {
    const n = Number(String(value ?? '').trim());
    return Number.isFinite(n) ? n : 0;
  }

  // ── Shared utilities ─────────────────────────────────────────────────────────

  /**
   * Accepts strict DD/MM/YYYY text dates and native Excel date cells (numeric serials, which
   * Excel stores for day ≤ 12 under a US-style locale). Returns null for all other formats.
   */
  private parseExcelDate(value: any): Date | null {
    let day: number, month: number, year: number;

    if (typeof value === 'number') {
      const code = XLSX.SSF.parse_date_code(value);
      if (!code || code.y < 1900) return null;
      ({ d: day, m: month, y: year } = code);
    } else if (typeof value === 'string') {
      const match = STRICT_DATE_DD_MM_YYYY_PATTERN.exec(value.trim());
      if (!match) return null;
      day = Number(match[1]);
      month = Number(match[2]);
      year = Number(match[3]);
    } else {
      return null;
    }

    if (month < 1 || month > 12) return null;

    const parsed = new Date(year, month - 1, day);

    return parsed.getFullYear() === year
      && parsed.getMonth() === month - 1
      && parsed.getDate() === day
      ? parsed
      : null;
  }

  /** Returns local calendar date YYYY-MM-DD, or empty string if unparseable. */
  private formatDateCell(value: any): string {
    const d = this.parseExcelDate(value);
    if (!d) return String(value ?? '').trim();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private formatDateCellIssue(value: any): string {
    const d = this.parseExcelDate(value);
    if (!d) return String(value ?? '').trim();
    const day = String(d.getDate()).padStart(2, '0');
    const month = d.toLocaleString('en-US', { month: 'short' });
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  }

  /** Returns whole days between now and the given date (positive = date is in the past). */
  private daysAgo(date: Date): number {
    const diffMs = Date.now() - date.getTime();
    return Math.floor(diffMs / (1000 * 60 * 60 * 24));
  }

  /** Strip *, normalise line-breaks and extra spaces for robust header comparison. */
  private normalizeHeader(value: any): string {
    return String(value ?? '').replace(/\*/g, '').replace(/\r\n|\r|\n/g, ' ').replace(/\s+/g, ' ').trim();
  }
}
